/**
 * TaskBoardTable Component
 *
 * Modern data table following 2025 design patterns:
 * - Clean, minimal column headers
 * - Status dots instead of full-width tags
 * - Avatar-style assignee display
 * - Relative time with smart formatting
 * - Overdue indicators
 * - Modern pagination with info display
 *
 * Design patterns applied:
 * - Data-first design
 * - Visual hierarchy with color coding
 * - Responsive column visibility
 */

import React, { useMemo, useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Pagination, Tooltip, Button, Skeleton } from 'antd';
import {
  MessageOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  LeftOutlined,
  RightOutlined,
  FieldTimeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import AppSpinner from '../shared/AppSpinner';
import TaskMobileCards, { TaskCardSkeleton } from '../shared/TaskMobileCards';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import {
  STATUS_CONFIG,
  URGENCY_CONFIG,
  getRowClassName,
  getRelativeTime,
  formatDueDate,
} from '../../utils/taskTableUtils';
import {
  getLastCommentUser,
  isUserAssignee,
} from '../../utils/task.utils';
import { icon, space } from '../../configs/designTokens';
import '../../styles/index.scss';

/**
 * Skeleton Loading Component for Task Table
 * Mirrors the actual table structure with responsive column visibility
 */
export const TaskTableSkeleton = ({ rows = 10, isMobile = false, isTablet = false, showAdvertiser = false }) => (
  <div className="task-table-skeleton" role="status" aria-label="Loading tasks">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="task-table-skeleton__row">
        {/* Task: urgency dot + status badge + name */}
        <div className="task-table-skeleton__cell task-table-skeleton__cell--name">
          <div className="task-table-skeleton__name-content">
            <Skeleton.Avatar active size={8} shape="circle" className="task-table-skeleton__urgency-dot" />
            <Skeleton.Button active size="small" style={{ width: 80, height: 20, minWidth: 80 }} />
            <Skeleton.Input active size="small" style={{ width: isMobile ? '50%' : '70%' }} />
          </div>
          {/* Mobile: inline due date */}
          {isMobile && (
            <Skeleton.Input active size="small" style={{ width: 60, marginTop: 4 }} />
          )}
        </div>

        {/* Assignee: avatar group - hide on mobile */}
        {!isMobile && (
          <div className="task-table-skeleton__cell task-table-skeleton__cell--assignee">
            <div className="task-table-skeleton__avatars">
              <Skeleton.Avatar active size={28} />
            </div>
          </div>
        )}

        {/* Due: date - hide on mobile */}
        {!isMobile && (
          <div className="task-table-skeleton__cell task-table-skeleton__cell--due">
            <Skeleton.Input active size="small" style={{ width: 70 }} />
          </div>
        )}

        {/* Type: request type - hide on tablet/mobile */}
        {!isMobile && !isTablet && (
          <div className="task-table-skeleton__cell task-table-skeleton__cell--type">
            <Skeleton.Input active size="small" style={{ width: 80 }} />
          </div>
        )}

        {/* Advertiser: org name - hide on tablet/mobile, platform only */}
        {!isMobile && !isTablet && showAdvertiser && (
          <div className="task-table-skeleton__cell task-table-skeleton__cell--advertiser">
            <Skeleton.Input active size="small" style={{ width: 100 }} />
          </div>
        )}

        {/* Updated: relative time */}
        <div className="task-table-skeleton__cell task-table-skeleton__cell--updated">
          <Skeleton.Input active size="small" style={{ width: 50 }} />
        </div>
      </div>
    ))}
  </div>
);

// TaskCardSkeleton is re-exported from shared/TaskMobileCards
export { TaskCardSkeleton };

const TaskBoardTable = ({
  tasks,
  loading,
  pagination,
  windowWidth,
  level,
  currentUser,
  onTaskClick,
  onTableChange,
  onPaginationChange,
  onPaginationSizeChange,
}) => {
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 992;
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const lastProcessedTasksRef = useRef(null);
  const [accumulatedTasks, setAccumulatedTasks] = useState([]);

  // Accumulate tasks for mobile infinite scroll
  useEffect(() => {
    if (!isMobile) {
      // Desktop: just use tasks directly
      setAccumulatedTasks(tasks);
      return;
    }

    // Skip if tasks haven't changed (same reference)
    if (tasks === lastProcessedTasksRef.current) {
      return;
    }

    // Skip empty arrays (loading state)
    if (!tasks || tasks.length === 0) {
      return;
    }

    // Mark these tasks as processed
    lastProcessedTasksRef.current = tasks;

    // Determine if this is a reset or load more by checking pagination
    if (pagination.current === 1) {
      // Reset - replace all
      setAccumulatedTasks([...tasks]);
    } else {
      // Load more - append (with deduplication)
      setAccumulatedTasks((prev) => {
        const existingIds = new Set(prev.map((t) => t.id));
        const newTasks = tasks.filter((t) => !existingIds.has(t.id));
        if (newTasks.length === 0) {
          return prev; // No new tasks, don't update
        }
        return [...prev, ...newTasks];
      });
    }

    // Reset loading flag when new data arrives
    loadingMoreRef.current = false;
  }, [tasks, isMobile, pagination]);

  // Use accumulated tasks for mobile, regular tasks for desktop
  const displayTasks = isMobile ? accumulatedTasks : tasks;

  // Infinite scroll for mobile - only true if we have some data and there's more to load
  const hasMorePages = accumulatedTasks.length > 0 && pagination.total > accumulatedTasks.length;

  const loadMore = useCallback(() => {
    if (!loading && hasMorePages && !loadingMoreRef.current) {
      loadingMoreRef.current = true;
      onPaginationChange({ page: pagination.current + 1, pageSize: pagination.pageSize });
    }
  }, [loading, hasMorePages, pagination, onPaginationChange]);

  useEffect(() => {
    if (!isMobile || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loading && !loadingMoreRef.current) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [isMobile, hasMorePages, loading, loadMore]);

  // Build columns based on level and screen size
  const columns = useMemo(() => {
    const cols = [];

    // Task Name column (always visible)
    // Tablet: wider column, no fixed position to avoid layout issues
    const taskColumnWidth = isMobile ? 180 : isTablet ? 280 : undefined;
    cols.push({
      title: 'Task',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      fixed: isMobile || isTablet ? false : 'left',
      width: taskColumnWidth,
      className: 'task-table__cell--name',
      render: (_, record) => {
        const statusConfig = STATUS_CONFIG[record.status] || {
          color: '#94a3b8',
          label: record.status
            ? record.status.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
            : 'Not Started',
          cssClass: 'todo',
        };
        const urgencyConfig = URGENCY_CONFIG[record.urgency] || URGENCY_CONFIG.LOW;
        const { isOverdue, daysLate } = formatDueDate(record.dueDate, record.status);

        // Handle click - normal click opens drawer, ctrl/cmd+click or middle-click opens in new tab
        const handleClick = (e) => {
          // Allow default behavior for ctrl+click, cmd+click, or middle-click (open in new tab)
          if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let the browser handle it (open in new tab)
          }
          e.preventDefault();
          onTaskClick(record.id);
        };

        return (
          <a
            href={`/tasks/details/${record.id}`}
            className="task-table__name-with-status"
            onClick={handleClick}
          >
            {/* Urgency indicator with tooltip */}
            <Tooltip title={urgencyConfig.label} placement="left">
              <span className={`task-table__urgency-indicator task-table__urgency-indicator--${urgencyConfig.cssClass}`} />
            </Tooltip>

            <div className="task-table__name-content">
              <div className="task-table__name">
                <span className="task-table__name-text">
                  {record.name || 'Untitled Task'}
                </span>
                {record.isRecurring && (
                  <Tooltip title="Recurring task">
                    <SyncOutlined className="task-table__recurring-icon" />
                  </Tooltip>
                )}
                {record.comments?.length > 0 && (
                  <span className="task-table__comment-badge">
                    <MessageOutlined /> {record.comments.length}
                  </span>
                )}
              </div>

              {/* Due date shown inline on mobile (no Due column) */}
              {isMobile && record.dueDate && (
                isOverdue ? (
                  <span className="task-table__overdue-pill">
                    <ExclamationCircleOutlined />
                    {daysLate}d late
                  </span>
                ) : (
                  <span className={`task-table__due-inline ${formatDueDate(record.dueDate, record.status).isToday ? 'task-table__due-inline--today' : ''}`}>
                    {formatDueDate(record.dueDate, record.status).text}
                  </span>
                )
              )}
            </div>

            {/* Status badge - placed after name for proper hierarchy */}
            <span className={`task-table__inline-status task-table__inline-status--${statusConfig.cssClass}`}>
              {statusConfig.label}
            </span>
          </a>
        );
      },
    });

    // Assignees column (hide on mobile)
    if (!isMobile) {
      cols.push({
        title: 'Assignee',
        dataIndex: 'assigneeUsers',
        key: 'assigneeUsers',
        width: 150,
        className: 'task-table__cell--assignee',
        render: (_, record) => {
          // Support both assigneeUsers (array) and assigneeUser (single)
          const assigneeList = record.assigneeUsers || (record.assigneeUser ? [record.assigneeUser] : []);

          // Awaiting icon logic for Assignee (matches develop):
          // Show when requestor made last comment OR no comments exist
          const lastCommentUser = getLastCommentUser(record);
          const isLastCommenterRequestor = lastCommentUser &&
            lastCommentUser.id === (record.createdBy && record.createdBy.id);
          const showAwaitingIcon = isLastCommenterRequestor ||
            !record.attachedComments ||
            (Array.isArray(record.attachedComments) && record.attachedComments.length === 0);

          // No assignee = no awaiting icon (matches develop)
          if (assigneeList.length === 0) {
            return <span className="task-table__unassigned">—</span>;
          }

          // Show max 3 avatars with overflow indicator
          const displayList = assigneeList.slice(0, 3);
          const overflowCount = assigneeList.length - 3;

          return (
            <div className="task-table__assignee-cell">
              <div className="task-table__assignee-group">
                {displayList.map((assignee) => {
                  const name = assignee.name || assignee.username || '';
                  return (
                    <Tooltip key={assignee.id} title={name}>
                      <div className="task-table__assignee-avatar">
                        <AvatarS3
                          imgKey={assignee.avatar?.key}
                          username={name}
                          size={28}
                        />
                      </div>
                    </Tooltip>
                  );
                })}
                {overflowCount > 0 && (
                  <Tooltip title={assigneeList.slice(3).map(a => a.name || a.username).join(', ')}>
                    <div className="task-table__assignee-overflow">
                      +{overflowCount}
                    </div>
                  </Tooltip>
                )}
              </div>
              {showAwaitingIcon && (
                <Tooltip title="Awaiting Response">
                  <FieldTimeOutlined className="task-table__awaiting-icon" />
                </Tooltip>
              )}
            </div>
          );
        },
      });
    }

    // Requestor column (hide on mobile)
    if (!isMobile) {
      cols.push({
        title: 'Requestor',
        dataIndex: 'createdBy',
        key: 'createdBy',
        width: 150,
        className: 'task-table__cell--requestor',
        render: (createdBy, record) => {
          // Awaiting icon logic for Requestor (matches develop):
          // Show when assignee or third party made the last comment
          const lastCommentUser = getLastCommentUser(record);
          const isLastCommenterAssignee = isUserAssignee(lastCommentUser, record);
          const isLastCommenterThirdParty = isUserAssignee(lastCommentUser, record, true);
          const showAwaitingIcon = isLastCommenterAssignee || isLastCommenterThirdParty;

          if (!createdBy) {
            return <span className="task-table__unassigned">—</span>;
          }

          const name = createdBy.name || createdBy.username || '';

          return (
            <div className="task-table__requestor-cell">
              <Tooltip title={name}>
                <div className="task-table__requestor-avatar">
                  <AvatarS3
                    imgKey={createdBy.avatar?.key}
                    username={name}
                    size={28}
                  />
                </div>
              </Tooltip>
              {showAwaitingIcon && (
                <Tooltip title="Awaiting Response">
                  <FieldTimeOutlined className="task-table__awaiting-icon" />
                </Tooltip>
              )}
            </div>
          );
        },
      });
    }

    // Due Date column (hide on mobile - shown inline with task name instead)
    if (!isMobile) {
      cols.push({
        title: 'Due',
        dataIndex: 'dueDate',
        key: 'dueDate',
        sorter: true,
        width: 150,
        className: 'task-table__cell--due',
        render: (_, record) => {
          const { text, isOverdue, isToday, daysLate } = formatDueDate(record.dueDate, record.status);

          if (text === '—') {
            return <span className="task-table__no-date">—</span>;
          }

          // Overdue pill badge
          if (isOverdue) {
            return (
              <Tooltip title={record.dueDate ? new Date(record.dueDate).toLocaleDateString() : ''}>
                <span className="task-table__overdue-pill">
                  <ExclamationCircleOutlined />
                  {daysLate}d late
                </span>
              </Tooltip>
            );
          }

          // Due today badge
          if (isToday) {
            return (
              <span className="task-table__due-today">
                <ClockCircleOutlined style={{ marginRight: space[1] }} />
                Today
              </span>
            );
          }

          return (
            <Tooltip title={record.dueDate ? new Date(record.dueDate).toLocaleDateString() : ''}>
              <span className="task-table__due">
                {text}
              </span>
            </Tooltip>
          );
        },
      });
    }

    // Request Type column (hide on tablet and mobile)
    if (!isMobile && !isTablet) {
      cols.push({
        title: 'Type',
        dataIndex: 'requestType',
        key: 'requestType',
        width: 180,
        className: 'task-table__cell--type',
        render: (_, record) => (
          <span className="task-table__type">
            {record.requestType || 'N/A'}
            <br />
            <span className="task-table__sub-type">{record.requestSubType || ''}</span>
          </span>
        ),
      });
    }

    // Advertiser column (platform only, hide on mobile/tablet)
    if (level !== 'CHANNEL_PARTNER' && !isMobile && !isTablet) {
      cols.push({
        title: 'Advertiser',
        dataIndex: 'advertiser',
        key: 'advertiser',
        width: 210,
        className: 'task-table__cell--advertiser',
        render: (_, record) => {
          const name = record.channelPartner?.name;

          return (
            <span className="task-table__advertiser" title={name}>
              {name || '—'}
            </span>
          );
        },
      });
    }

    // Created column
    cols.push({
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      width: 120,
      className: 'task-table__cell--created',
      render: (_, record) => (
        <Tooltip
          title={
            record.createdAt
              ? new Date(record.createdAt).toLocaleString()
              : ''
          }
        >
          <span className="task-table__created">
            {record.createdAt ? new Date(record.createdAt).toLocaleDateString() : '—'}
          </span>
        </Tooltip>
      ),
    });

    // Updated column
    cols.push({
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: true,
      width: 120,
      className: 'task-table__cell--updated',
      render: (_, record) => (
        <Tooltip
          title={
            record.updatedAt
              ? new Date(record.updatedAt).toLocaleString()
              : ''
          }
        >
          <span className="task-table__updated">
            {record.updatedAt ? getRelativeTime(record.updatedAt) : '—'}
          </span>
        </Tooltip>
      ),
    });

    return cols;
  }, [level, isMobile, isTablet, onTaskClick]);

  // Pagination calculations
  const paginationStart = pagination.total > 0 ? (pagination.current - 1) * pagination.pageSize + 1 : 0;
  const paginationEnd = Math.min(pagination.current * pagination.pageSize, pagination.total);
  const progressPercent = pagination.total > 0 ? (accumulatedTasks.length / pagination.total) * 100 : 0;

  // Custom pagination item renderer for Previous/Next text
  const itemRender = (_page, type, originalElement) => {
    if (type === 'prev') {
      return (
        <Button type="text" icon={<LeftOutlined />}>
          Previous
        </Button>
      );
    }
    if (type === 'next') {
      return (
        <Button type="text">
          Next
          <RightOutlined />
        </Button>
      );
    }
    return originalElement;
  };

  return (
    <div className="task-board-table-modern">
      {!isMobile && (
        <Table
          columns={columns}
          dataSource={displayTasks}
          loading={{
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: icon.xxl }} spin />,
          }}
          pagination={false}
          scroll={{ x: isTablet ? 1000 : 1400 }}
          className="task-table"
          onChange={onTableChange}
          rowKey={(record) => record.id}
          rowClassName={(record) => getRowClassName(record)}
          size="middle"
        />
      )}

      {isMobile && (
        <TaskMobileCards
          tasks={displayTasks}
          loading={loading}
          onTaskClick={onTaskClick}
          level={level}
        />
      )}

      {/* Desktop/Tablet: Enhanced Three-Column Pagination */}
      {!isMobile && (
        <div className="task-pagination-enhanced">
          <div className="task-pagination-enhanced__left">
            <span className="task-pagination-enhanced__summary">
              {pagination.total > 0 ? (
                <>
                  Showing <strong>{paginationStart}</strong>
                  {' - '}
                  <strong>{paginationEnd}</strong>
                  {' of '}
                  <strong>{pagination.total}</strong> tasks
                </>
              ) : (
                'No tasks found'
              )}
            </span>
          </div>

          <div className="task-pagination-enhanced__center">
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={false}
              hideOnSinglePage={false}
              onChange={(page) => onPaginationChange({ page, pageSize: pagination.pageSize })}
              itemRender={itemRender}
              className="task-pagination-enhanced__nav"
              showLessItems={isTablet}
            />
          </div>

          <div className="task-pagination-enhanced__right">
            <span className="task-pagination-enhanced__size-label">Show:</span>
            <div className="task-pagination-enhanced__size-options">
              {[10, 25, 50, 100].map((size) => (
                <button
                  key={size}
                  className={`task-pagination-enhanced__size-btn ${pagination.pageSize === size ? 'task-pagination-enhanced__size-btn--active' : ''}`}
                  onClick={() => onPaginationSizeChange(1, size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Infinite Scroll */}
      {isMobile && pagination.total > 0 && (
        <div className="task-mobile-pagination">
          {/* Progress indicator */}
          <div className="task-mobile-pagination__progress">
            <div className="task-mobile-pagination__progress-bar">
              <div
                className="task-mobile-pagination__progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="task-mobile-pagination__progress-text">
              {accumulatedTasks.length} of {pagination.total}
            </span>
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="task-mobile-pagination__sentinel">
            {loading && hasMorePages && (
              <div className="task-mobile-pagination__loading">
                <AppSpinner size="small" />
                <span>Loading more...</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

TaskBoardTable.propTypes = {
  tasks: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  pagination: PropTypes.object.isRequired,
  windowWidth: PropTypes.number.isRequired,
  level: PropTypes.string,
  currentUser: PropTypes.object,
  onTaskClick: PropTypes.func.isRequired,
  onTableChange: PropTypes.func.isRequired,
  onPaginationChange: PropTypes.func.isRequired,
  onPaginationSizeChange: PropTypes.func.isRequired,
};

TaskBoardTable.defaultProps = {
  loading: false,
  level: '',
  currentUser: null,
};

export default TaskBoardTable;
